import { supabase } from '@/lib/supabase';

export interface MediaPost {
  id: string;
  owner_id: string;
  title: string;
  description: string | null;
  category: string;
  content_type: string | null;
  video_url: string;
  thumbnail_url: string | null;
  artist_name: string | null;
  location: string | null;
  views: number;
  likes: number;
  status: string;
  created_at: string;
}

export interface MediaTrack {
  id: string;
  owner_id: string;
  title: string;
  artist_name: string | null;
  category: string;
  content_type: string | null;
  audio_url: string;
  cover_url: string | null;
  duration: number | null;
  plays: number;
  likes: number;
  location: string | null;
  status: string;
  created_at: string;
}

export const MEDIA_CATEGORIES = [
  'Musique',
  'Danse',
  'Comédie',
  'Tutoriel',
  'Artisanat',
  'Mode & Wax',
  'Gastronomie',
  'Tradition',
  'Culture',
  'Contes & Légendes',
  'Théâtre',
  'Langues & Traditions',
  'Publicité',
  'Acapella',
];

export const MEDIA_BUCKET = 'media';

// Seuil de vues (sur une seule vidéo) pour certifier automatiquement un artiste.
export const CERTIFICATION_VIEWS = 1_000_000;

export function getCertifiedArtists(posts: MediaPost[]): Set<string> {
  const certified = new Set<string>();
  for (const p of posts) {
    if (p.artist_name && (p.views || 0) >= CERTIFICATION_VIEWS) {
      certified.add(p.artist_name);
    }
  }
  return certified;
}

export interface NewPostInput {
  title: string;
  description: string;
  category: string;
  contentType: string;
  artistName: string;
  location: string;
}

export interface NewTrackInput {
  title: string;
  category: string;
  contentType: string;
  artistName: string;
  location: string;
}

export async function fetchMediaPosts(): Promise<MediaPost[]> {
  const { data, error } = await supabase
    .from('media_posts')
    .select('*')
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as MediaPost[]) || [];
}

export async function fetchMediaTracks(): Promise<MediaTrack[]> {
  const { data, error } = await supabase
    .from('media_tracks')
    .select('*')
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as MediaTrack[]) || [];
}

async function uploadToBucket(file: File, folder: string): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
  const path = `${folder}/${safeName}`;

  const { error } = await supabase.storage.from(MEDIA_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(MEDIA_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function uploadCoverImage(file: File): Promise<string> {
  return uploadToBucket(file, 'covers');
}

export async function publishVideo(
  file: File,
  thumbnailUrl: string | null,
  input: NewPostInput,
): Promise<MediaPost> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData?.session?.user;
  if (!user) throw new Error('Utilisateur non connecté.');

  const videoUrl = await uploadToBucket(file, 'videos');

  const { data, error } = await supabase
    .from('media_posts')
    .insert({
      owner_id: user.id,
      title: input.title,
      description: input.description,
      category: input.category,
      content_type: input.contentType,
      video_url: videoUrl,
      thumbnail_url: thumbnailUrl,
      artist_name: input.artistName,
      location: input.location,
    })
    .select()
    .single();
  if (error) throw error;
  return data as MediaPost;
}

export async function publishVideoByLink(
  url: string,
  thumbnailUrl: string | null,
  input: NewPostInput,
): Promise<MediaPost> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData?.session?.user;
  if (!user) throw new Error('Utilisateur non connecté.');

  let thumbnail = thumbnailUrl;
  if (isYoutubeUrl(url) && !thumbnail) {
    const id = getYoutubeId(url);
    if (id) thumbnail = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }

  const { data, error } = await supabase
    .from('media_posts')
    .insert({
      owner_id: user.id,
      title: input.title,
      description: input.description,
      category: input.category,
      content_type: input.contentType,
      video_url: url,
      thumbnail_url: thumbnail,
      artist_name: input.artistName,
      location: input.location,
    })
    .select()
    .single();
  if (error) throw error;
  return data as MediaPost;
}

export async function publishTrack(
  file: File,
  coverUrl: string | null,
  input: NewTrackInput,
): Promise<MediaTrack> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData?.session?.user;
  if (!user) throw new Error('Utilisateur non connecté.');

  const audioUrl = await uploadToBucket(file, 'audio');

  const { data, error } = await supabase
    .from('media_tracks')
    .insert({
      owner_id: user.id,
      title: input.title,
      category: input.category,
      content_type: input.contentType,
      audio_url: audioUrl,
      cover_url: coverUrl,
      artist_name: input.artistName,
      location: input.location,
    })
    .select()
    .single();
  if (error) throw error;
  return data as MediaTrack;
}

export async function incrementPostView(id: string): Promise<void> {
  await supabase.rpc('increment_post_view', { p_id: id });
}

export async function incrementTrackPlay(id: string): Promise<void> {
  await supabase.rpc('increment_track_play', { p_id: id });
}

export async function likePost(id: string): Promise<void> {
  await supabase.rpc('like_media_post', { p_id: id });
}

export async function likeTrack(id: string): Promise<void> {
  await supabase.rpc('like_media_track', { p_id: id });
}

export function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

export function isYoutubeUrl(url: string): boolean {
  return /(youtube\.com|youtu\.be)/i.test(url);
}

export function isTiktokUrl(url: string): boolean {
  return /tiktok\.com/i.test(url);
}

export function isSupportedMediaUrl(url: string): boolean {
  return isYoutubeUrl(url) || isTiktokUrl(url);
}

export function getYoutubeId(url: string): string | null {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/,
  );
  return m ? m[1] : null;
}

export function toYoutubeEmbedUrl(url: string): string {
  const id = getYoutubeId(url);
  return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1` : '';
}